import type {
	FilterRule,
	KanbanConfig,
	MobileCardConfiguration,
} from '../../types';

/**
 * Zones mobile card for client lists (PD-14059). The title column already
 * carries name + wrapped street / city-state-zip address lines, so the body
 * pairs the tappable phone/email rows wide-left (labels blanked — the link
 * values are self-describing) with Rep · Alt right-aligned beside them,
 * replacing the audit's right-side wall of stacked names and contacts.
 * Lifecycle stage joins the lead/customer + business-type badges in the
 * footer. Reorder forecasting stays desktop-only. Shared across every
 * client list — zone refs that don't match a list's column set (near-me,
 * activity lists) silently no-op.
 */
export const CLIENT_MOBILE_CARD_CONFIG: MobileCardConfiguration = {
	cardLayout: 'zones',
	primaryFields: ['client-name'],
	wideFields: ['contact-info'],
	// The stage selector is a footer badge either way it is laid out: its own
	// column on a pre-PD-13163 saved layout, a field of the salespeople column
	// on the bundled one.
	badgeFields: ['lifecycle-stage', 'salespeople.lifecycle-stage-name'],
	hiddenFields: ['reorder'],
	labelOverrides: {
		'contact-info.phone': '',
		'contact-info.email': '',
		// "Salesperson" / "Alternate" eat the right column before the
		// names start.
		'salespeople.salesperson-name': 'Rep',
		'salespeople.alternate-salesperson-name': 'Alt',
		// The sparkline reads as a sales trend on sight, and on a phone the
		// label costs more width than it explains.
		salesTrend: '',
	},
};

/**
 * Shared kanban configuration for client lists
 */
export const createClientKanbanConfig = (): KanbanConfig => ({
	cardLayout: {
		sections: {
			header: [
				{
					id: 'card-name',
					link: {
						type: 'object',
						enabled: true,
						objectTable: 'client',
					},
					dataConfig: {
						type: 'field',
						field: {
							fieldName: 'name',
							objectTable: 'client',
						},
					},
					styleConfig: {
						size: 'sm',
						colorRule: {
							type: 'default',
						},
						fontWeight: 'semibold',
					},
					displayStyle: 'text',
				},
			],
			body: [
				{
					id: 'card-address',
					dataConfig: {
						type: 'field',
						field: {
							fieldName: 'address1',
							objectTable: 'client',
						},
					},
					styleConfig: {
						size: 'xs',
						colorRule: {
							type: 'theme',
							themeColor: 'textSecondary',
						},
					},
					displayStyle: 'text',
					conditionalVisibility: {
						field: 'address1',
						condition: 'is_not_null',
					},
				},
				{
					id: 'card-city-state',
					dataConfig: {
						type: 'template',
						template: '{client.city}, {client.state} {client.zip}',
						templateFields: [
							{ fieldName: 'city' },
							{ fieldName: 'state' },
							{ fieldName: 'zip' },
						],
					},
					styleConfig: {
						size: 'xs',
						colorRule: {
							type: 'theme',
							themeColor: 'textSecondary',
						},
					},
					displayStyle: 'text',
					conditionalVisibility: {
						field: 'city',
						condition: 'is_not_null',
					},
				},
			],
			footer: [
				{
					id: 'card-client-id',
					displayStyle: 'badge',
					dataConfig: {
						type: 'template',
						template:
							'{leadOrCustomer:Customer=C,Lead=L}#{externalId2|externalId|id}',
						templateFields: [
							{ fieldName: 'leadOrCustomer' },
							{ fieldName: 'externalId2' },
							{ fieldName: 'externalId' },
							{ fieldName: 'id' },
						],
					},
					styleConfig: {
						size: 'xs',
						colorRule: {
							type: 'conditional',
							rules: [
								{
									field: {
										fieldName: 'leadOrCustomer',
									},
									operator: 'equals',
									value: 'Customer',
									ignoreCase: true,
									color: { type: 'info' },
								},
								{
									field: {
										fieldName: 'leadOrCustomer',
									},
									operator: 'equals',
									value: 'Lead',
									ignoreCase: true,
									color: { type: 'warning' },
								},
							],
						},
						variant: 'solid',
					},
				},
				{
					id: 'card-salesperson',
					dataConfig: {
						type: 'field',
						field: {
							fieldName: 'salespersonName',
							objectTable: 'client',
						},
					},
					styleConfig: {
						size: 'xs',
						colorRule: {
							type: 'theme',
							themeColor: 'textSecondary',
						},
					},
					displayStyle: 'text',
				},
			],
		},
	},
	groupByField: {
		fieldName: 'fkClientLifecycleStageId',
		objectTable: 'client',
	},
	filterKey: 'lifecycleStage',
	ordinalField: 'lifecycleOrdinal',
	onColumnChange: 'onLifecycleStageChange',
	onCardClick: 'onCardClick',
	columnLabelField: {
		fieldName: 'name',
		objectTable: 'lifecycleStage',
	},
	columnOrderField: {
		fieldName: 'ordinal',
		objectTable: 'lifecycleStage',
	},
	headerAggregations: [
		{
			type: 'count',
			label: 'Count',
		},
	],
	allowDragBetweenColumns: true,
	showUnassignedColumn: true,
});

/**
 * Standard salespeople filter rules for the broad client lists ("All Clients", etc).
 * Users with clients:list:any (Salesperson, Sales Manager, Admin) see all clients
 * and have no salespeople filter default or bound. Users without list:any get the
 * permission-gated bounded set with ME defaulted in.
 *
 * ME appears if user has clients:view:self; Unassigned appears if user has clients:view:unassigned.
 */
export const SALESPEOPLE_FILTER_RULES: Record<string, FilterRule> = {
	salespeople: {
		defaultValue: [],
		additionalValues: [
			{
				values: [{ id: 'ME', label: 'Me' }],
				applyIf: {
					type: 'group',
					joiner: 'AND',
					negated: false,
					children: [
						{ type: 'rule', action: 'clients:view:self', negated: false },
					],
				},
			},
			{
				values: [{ id: 'ME_ALTERNATE', label: 'Me (Alternate)' }],
				applyIf: {
					type: 'group',
					joiner: 'AND',
					negated: false,
					children: [
						{ type: 'rule', action: 'clients:view:self', negated: false },
					],
				},
			},
			{
				values: [{ id: 'null', label: 'Unassigned' }],
				applyIf: {
					type: 'group',
					joiner: 'AND',
					negated: false,
					children: [
						{ type: 'rule', action: 'clients:view:unassigned', negated: false },
					],
				},
			},
		],
		applyDefaultIf: {
			type: 'group',
			joiner: 'AND',
			negated: false,
			children: [{ type: 'rule', action: 'clients:list:any', negated: true }],
		},
		boundedIf: {
			type: 'group',
			joiner: 'AND',
			negated: false,
			children: [{ type: 'rule', action: 'clients:list:any', negated: true }],
		},
	},
};

/**
 * Salesperson filter rules that default to only "Me" (self).
 * Alternate and Unassigned are available in the bounded set but not applied as defaults.
 * Use this for tabs where users should see only their own clients by default
 * (e.g., Reorder, Contact Due). All filter enforcement (default + bound) is tied to
 * clients:list:any: users with that perm (including salespeople in tenants that
 * grant it) see all clients freely; users without it default to ME and are bounded
 * to the permission-gated set.
 */
export const SALESPEOPLE_FILTER_RULES_SELF_DEFAULT = {
	salespeople: {
		defaultValue: [],
		additionalValues: [
			{
				values: [{ id: 'ME', label: 'Me' }],
				applyIf: {
					type: 'group',
					joiner: 'AND',
					negated: false,
					children: [
						{ type: 'rule', action: 'clients:view:self', negated: false },
					],
				},
			},
			{
				values: [{ id: 'ME_ALTERNATE', label: 'Me (Alternate)' }],
				applyIf: {
					type: 'group',
					joiner: 'AND',
					negated: false,
					children: [
						{ type: 'rule', action: 'clients:view:self', negated: false },
					],
				},
				applyAsDefault: false as const,
			},
			{
				values: [{ id: 'null', label: 'Unassigned' }],
				applyIf: {
					type: 'group',
					joiner: 'AND',
					negated: false,
					children: [
						{ type: 'rule', action: 'clients:view:unassigned', negated: false },
					],
				},
				applyAsDefault: false as const,
			},
		],
		applyDefaultIf: {
			type: 'group',
			joiner: 'AND',
			negated: false,
			children: [{ type: 'rule', action: 'clients:list:any', negated: true }],
		},
		boundedIf: {
			type: 'group',
			joiner: 'AND',
			negated: false,
			children: [{ type: 'rule', action: 'clients:list:any', negated: true }],
		},
	},
} satisfies Record<string, FilterRule>;

/**
 * Standard active filters for activity-based lists
 */
export const ACTIVITY_LIST_ACTIVE_FILTERS = [
	'clientSearch',
	'clientCompany',
	'salespeople',
	'alternateRep',
	'businessTypes',
	'leadOrCustomer',
	'salesStatus',
	'callStatus',
	// PD-15407: State replaces the combined geography search as the default
	// location filter (geography stays registered / re-addable).
	'clientState',
	'revenue90Days',
	'orderSalesStatus',
	'groups',
	'specialFilters',
	'clientLists',
	'excludeClientLists',
	'clientDeletedRecords',
];

/**
 * Standard required fields for activity lists
 */
export const ACTIVITY_LIST_REQUIRED_FIELDS = [
	'client._id',
	'client.address1',
	'client.averageInvoiceRevenue',
	'client.businessType',
	'client.city',
	'client.companyNames',
	'client.email',
	'client.externalId',
	'client.externalId2',
	'client.fax',
	'client.forecast',
	'client.forecastedReorderDate',
	'client.googlePlaceId',
	'client.latitude',
	'client.leadOrCustomer',
	'client.longitude',
	// PD-13163 — the 1Y Sales sparkline's series and trend. An authored
	// `requiredFields` array wins over the component's own declared
	// requirements (ListConfigService.getRequiredFieldsForList prefers the
	// override), so a chart column's data has to be named here or it never
	// gets requested.
	'client.monthlyData',
	'client.name',
	'client.nextActivityDate',
	'client.nextActivityTaskId',
	'client.phone',
	'client.salesStatus',
	'client.state',
	'client.trend',
	'client.zip',
];

/**
 * Standard required fields for default client lists
 */
export const DEFAULT_LIST_REQUIRED_FIELDS = [
	'client._id',
	'client.address1',
	'client.alternateSalespersonName',
	'client.averageInvoiceRevenue',
	'client.businessType',
	'client.city',
	'client.companyNames',
	'client.email',
	'client.externalId',
	'client.externalId2',
	'client.fax',
	'client.fkClientLifecycleStageId',
	// Salesperson IDs are required so the frontend can suppress detail-page
	// links to clients the caller cannot open (see canViewClientDetail).
	'client.fkUserId',
	'client.fkUserIdAlternate',
	// The Reorder Due column shows the forecast amount above the date. Without
	// it here the amount rendered as "-" on every row of these lists.
	'client.forecast',
	'client.forecastedReorderDate',
	'client.googlePlaceId',
	'client.latitude',
	'client.leadOrCustomer',
	'client.lifecycleStageColor',
	'client.lifecycleStageName',
	'client.longitude',
	// PD-13163 — the 1Y Sales sparkline's series and trend. An authored
	// `requiredFields` array wins over the component's own declared
	// requirements (ListConfigService.getRequiredFieldsForList prefers the
	// override), so a chart column's data has to be named here or it never
	// gets requested.
	'client.monthlyData',
	'client.name',
	'client.nextActivityDate',
	'client.phone',
	'client.salesStatus',
	'client.salespersonName',
	'client.state',
	'client.trend',
	'client.zip',
];
